import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Stack,
  IconButton,
  Chip,
} from "@mui/material";
import { StyledCellHeader } from "../../../../components/StyledCellHeader";
import { Receipt as ReceiptIcon, Payment as PaymentIcon, RemoveRedEyeOutlined } from "@mui/icons-material";
import DetallePagosDialog from "./detallepagos";
import AlertMessageDialog from "../../../../components/Dialogs/alertMessageDialog";
import OnGeneralMessageDialog from "../../../../components/Dialogs/OnGeneralMessageDialog";
import { formatNumberWithCommas } from "../../../../utils/generalFunctions";
import { cancelAbonoCapital } from "../../../../utils/generalsFetch";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";

const currencyRD = (v) => {
  if (typeof v === 'string') {
    const numValue = parseFloat(v.replace(/,/g, ''));
    if (isNaN(numValue)) return 'RD$0.00';
    return new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(numValue);
  }
  if (typeof v === 'number' && !isNaN(v)) {
    return new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(v);
  }
  return 'RD$0.00';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function HistorialPagosPrestamo(props) {
  const [pagos, setPagos] = React.useState([]);
  const [selectedPago, setSelectedPago] = React.useState(null);
  const [openDetailDialog, setOpenDetailDialog] = React.useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertSeverity, setAlertSeverity] = React.useState('success');
  const [openCancelConfirmDialog, setOpenCancelConfirmDialog] = React.useState(false);
  const [pagoToCancel, setPagoToCancel] = React.useState(null);

  React.useEffect(() => {
    fetchPagos(props.prestamoId);
  }, [props.prestamoId]);

  async function fetchPagos(prestamoId) {
    const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/pagos-prestamo/${prestamoId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany'),
      },
    });
    const data = await response.json();
    setPagos(data["pagos"]);
  }

  const handleViewDetails = (pago) => {
    setSelectedPago(pago);
    setOpenDetailDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDetailDialog(false);
    setSelectedPago(null);
  };

  const showAlert = (message, severity = 'success') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  const handleCancelPaymentRequest = (pago) => {
    setPagoToCancel(pago);
    setOpenCancelConfirmDialog(true);
  };

  const handleCloseCancelConfirmDialog = () => {
    setOpenCancelConfirmDialog(false);
    setPagoToCancel(null);
  };

  const handlePrintReceipt = (pago) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Generate HTML content for the receipt
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Recibo de Pago - ${pago.id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              line-height: 1.6;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .company-name {
              font-size: 24px;
              font-weight: bold;
              color: #1976d2;
            }
            .receipt-title {
              font-size: 20px;
              margin-top: 10px;
            }
            .receipt-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
            }
            .info-section {
              flex: 1;
              margin: 0 10px;
            }
            .info-label {
              font-weight: bold;
              color: #666;
            }
            .info-value {
              margin-bottom: 10px;
            }
            .amount-section {
              text-align: center;
              background-color: #f5f5f5;
              padding: 20px;
              margin: 20px 0;
              border-radius: 8px;
            }
            .amount-label {
              font-size: 16px;
              color: #666;
            }
            .amount-value {
              font-size: 28px;
              font-weight: bold;
              color: #1976d2;
              margin-top: 5px;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">BANKIVO</div>
            <div class="receipt-title">RECIBO DE PAGO</div>
          </div>
          
          <div class="receipt-info">
            <div class="info-section">
              <div class="info-label">Fecha de Pago:</div>
              <div class="info-value">${formatDate(pago.pago_fecha)}</div>
              
              <div class="info-label">ID de Pago:</div>
              <div class="info-value">${pago.id}</div>
            </div>
            
            <div class="info-section">
              <div class="info-label">Procesado Por:</div>
              <div class="info-value">${pago.usuario_nombre || 'Usuario del Sistema'}</div>
              
              <div class="info-label">Sucursal:</div>
              <div class="info-value">${pago.sucursal_name || 'N/A'}</div>
            </div>
          </div>
          
          <div class="amount-section">
            <div class="amount-label">MONTO PAGADO</div>
            <div class="amount-value">${currencyRD(pago.monto_pagado)}</div>
          </div>
          
          <div class="info-section">
            <div class="info-label">Método de Pago:</div>
            <div class="info-value">
              ${JSON.parse(pago.metodo_pago).map(metodo => 
                `${metodo.method}: ${formatNumberWithCommas(metodo.amount)}`
              ).join(', ')}
            </div>
          </div>
          
          <div class="footer">
            <p>Este recibo fue generado automáticamente el ${new Date().toLocaleString('es-ES')}</p>
            <p>Gracias por su pago</p>
          </div>
        </body>
      </html>
    `;
    
    // Write the HTML content to the new window
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const handleCancelPayment = async (pago) => {
    try {
        if (pago.tipo === 'abono_capital') {
          await cancelAbonoCapital({
            idcompany: localStorage.getItem('idcompany'),
            usuarioId: localStorage.getItem('id'),
            pagoId: pago.id,
            payload: { id_usuario: localStorage.getItem('id') },
          });
          await fetchPagos(props.prestamoId);
          handleCloseDialog();
          handleCloseCancelConfirmDialog();
          showAlert('Abono a capital cancelado y amortización restaurada', 'success');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/pagos-prestamo/${pago.id}/${props.metodo == "plazo_indefinido" ? "plazo_indefinido/cancelar" : "cancelar"}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany'),
            },
            body: JSON.stringify({
              id_prestamo: props.prestamoId,
              id_pago_prestamo: pago.id,
            }),
          });

          if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            throw new Error(errBody.message || 'Error al cancelar el pago');
          }

          // Refresh the payments list
          await fetchPagos(props.prestamoId);

          // Close the dialogs
          handleCloseDialog();
          handleCloseCancelConfirmDialog();

          // Show success message
          showAlert('Pago cancelado exitosamente', 'success');

    } catch (error) {
      console.error('Error al cancelar el pago:', error);
      showAlert(error.message || 'Error al cancelar el pago. Intente nuevamente.', 'error');
    }
  };

  return (
    <div>
      {pagos.length > 0 ? (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "none", minHeight: "500px", mt:2 }}>
          <Table sx={{padding:1}}>
            <TableHead>
              <TableRow>
                <StyledCellHeader sx={{textAlign: 'center'}}>Fecha</StyledCellHeader>
                <StyledCellHeader sx={{textAlign: 'center'}}>Monto</StyledCellHeader>
                <StyledCellHeader sx={{textAlign: 'center'}}>Procesado Por</StyledCellHeader>
                <StyledCellHeader sx={{textAlign: 'center'}}>Estado</StyledCellHeader>
                <StyledCellHeader></StyledCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagos.map((pago, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{textAlign: 'center'}}>{pago.fecha_pago}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <span>{pago.monto_pagado}</span>
                      {pago.tipo === 'abono_capital' && (
                        <Chip
                          icon={<SavingsOutlinedIcon />}
                          label="Abono a Capital"
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{textAlign: 'center'}}>{pago.usuario_nombre}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>
                    <Chip label={pago.estado} color={pago.estado === 'cancelado' ? 'error' : 'success'} size="small" />
                  </TableCell>
                   <TableCell>
                     <IconButton
                       size="small"
                       sx={{ color: "#617689" }}
                       onClick={() => handleViewDetails(pago)}
                     >
                       <RemoveRedEyeOutlined />
                     </IconButton>
                   </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper 
          sx={{ 
            borderRadius: 3, 
            boxShadow: "none", 
            minHeight: "500px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fafafa",
            border: "2px dashed #e0e0e0"
          }}
        >
          <Box sx={{ textAlign: "center", py: 8, px: 4 }}>
            <Stack spacing={3} alignItems="center">
              {/* Icon Container */}
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  backgroundColor: "#e3f2fd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2
                }}
              >
                <PaymentIcon 
                  sx={{ 
                    fontSize: 60, 
                    color: "#1976d2",
                    opacity: 0.7
                  }} 
                />
              </Box>

              {/* Main Message */}
              <Typography 
                variant="h5" 
                fontWeight={700} 
                color="text.primary"
                sx={{ mb: 1 }}
              >
                No hay pagos registrados
              </Typography>

              {/* Subtitle */}
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ maxWidth: 400, lineHeight: 1.6 }}
              >
                Este préstamo aún no tiene pagos registrados. Los pagos aparecerán aquí una vez que se procesen.
              </Typography>

              {/* Additional Info */}
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  p: 3,
                  mt: 2,
                  border: "1px solid #e0e0e0"
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <ReceiptIcon sx={{ color: "#666", fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Los pagos se registrarán automáticamente cuando se procesen
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Paper>
      )}
      
      {/* Payment Details Dialog */}
      {selectedPago && (
      <DetallePagosDialog
        open={openDetailDialog}
        onClose={handleCloseDialog}
        pago={selectedPago}
        prestamo={props.prestamo}
        onCancelPayment={handleCancelPaymentRequest}
        onPrintReceipt={handlePrintReceipt}
      />
      )}

      {/* Alert Message Dialog */}
      <AlertMessageDialog
        open={alertOpen}
        handleClose={handleCloseAlert}
        message={alertMessage}
        severity={alertSeverity}
        title=""
      />

      {/* Cancel Payment Confirmation Dialog */}
      <OnGeneralMessageDialog
        open={openCancelConfirmDialog}
        onClose={handleCloseCancelConfirmDialog}
        title={
          pagoToCancel?.tipo === 'abono_capital'
            ? '¿Cancelar este abono a capital?'
            : '¿Estás seguro de cancelar este pago?'
        }
        message={
          pagoToCancel?.tipo === 'abono_capital'
            ? 'La tabla de amortización volverá al estado anterior al abono y el ajuste se registrará en su caja actual. Solo se permite si no hay pagos posteriores ni cuotas regeneradas pagadas.'
            : 'Esta acción cancelará permanentemente el pago registrado. Esta operación no se puede deshacer y afectará el estado del préstamo. ¿Deseas continuar?'
        }
        buttonText={pagoToCancel?.tipo === 'abono_capital' ? 'Sí, cancelar abono' : 'Sí, Cancelar Pago'}
        onRegister={() => handleCancelPayment(pagoToCancel)}
        buttonColor="error"
      />
    </div>
  );
}

    
    